/* eslint-disable camelcase */

import { useCallback } from 'react';

import { useApiFreelancerPartialUpdate } from 'state/__generated__/tadaahAPI';
import type { Address } from 'state/__generated__/tadaahAPI';
import type { ErrorType } from 'state/custom-instance';
import client from 'state/remote/client';

import { useGetFreelancerDetails } from './use-get-freelancer-details';

export type UpdateFreelancerPersonalInformationType = {
  about: string;
  city: string;
  firstName: string;
  lastName: string;
  houseNumber: string;
  houseNumberAddition: string;
  phoneNumber: string;
  postalCode: string;
  street: string;
};

type UseUpdateFreelancerPersonalInformationHandler = {
  initialValues: UpdateFreelancerPersonalInformationType;
  error: ErrorType<unknown> | null;
  isLoading: boolean;
  isSuccess: boolean;
  updateFreelancerPersonalInformation: (
    values: UpdateFreelancerPersonalInformationType,
  ) => Promise<void>;
};

const useUpdateFreelancerPersonalInformation =
  (): UseUpdateFreelancerPersonalInformationHandler => {
    const { data } = useGetFreelancerDetails();
    const { mutateAsync, isLoading, isSuccess, error } =
      useApiFreelancerPartialUpdate({
        mutation: {
          mutationKey: 'update-freelancer-personal-information',

          onSuccess: () => {
            const queriesToInvalidate = [
              'freelancer-profile',
              'freelancer-details',
              'freelancer-signup-status',
            ];
            queriesToInvalidate.forEach((query) => {
              client.invalidateQueries(query);
            });
          },
        },
      });

    const updateFreelancerPersonalInformation = useCallback(
      async (values: UpdateFreelancerPersonalInformationType) => {
        const raw_address = `${values.street} ${values.houseNumber} ${values.houseNumberAddition}`;

        const address = {
          address_type: 'Home' as const,
          city: values.city,
          house_number: values.houseNumber,
          house_number_extension: values.houseNumberAddition,
          postal_code: values.postalCode,
          raw_address,
          street_name: values.street,
        };

        const existingHomeAddress = data?.addresses?.find(
          (adr) => adr.address_type === 'Home',
        );

        if (existingHomeAddress)
          Object.assign(address, { id: existingHomeAddress.id });

        const response = await mutateAsync({
          // @ts-expect-error wrong type information
          data: {
            about: values.about,
            addresses: [address],
            first_name: values.firstName,
            last_name: values.lastName,
            phone_number: values.phoneNumber,
          },
        });
        if (response.status !== 200) throw new Error('Something went wrong');
      },
      [data?.addresses, mutateAsync],
    );

    const homeAddress = data?.addresses?.find(
      (address: Address) => address.address_type === 'Home',
    );

    const initialValues = {
      about: data?.about ?? '',
      address: homeAddress?.raw_address,
      city: homeAddress?.city ?? '',
      firstName: data?.first_name ?? '',
      houseNumber: homeAddress?.house_number ?? '',
      houseNumberAddition: homeAddress?.house_number_extension ?? '',
      lastName: data?.last_name ?? '',
      phoneNumber: data?.phone_number ?? '',
      postalCode: homeAddress?.postal_code ?? '',
      street: homeAddress?.street_name ?? '',
    };
    return {
      error,
      initialValues,
      isLoading,
      isSuccess,
      updateFreelancerPersonalInformation,
    };
  };

export default useUpdateFreelancerPersonalInformation;
